import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { EMPTY_OBJECT, useTenderDetails } from '../../contexts/TenderDetailsContext';
import { ARCHETYPE_LABELS, DOMAIN_ARCHETYPES } from '../../types/knowledge';
import { StandardTable } from '../common/UI/StandardTable';
import SupportDocumentsSection from './SupportDocumentsSection';
import WorkflowStep from './WorkflowStep';

const ManageDocumentsStep: React.FC = () => {
  const {
    canManageTender,
    openStep,
    setOpenStep,
    stepCompletion,
    toggleStepCompletion,
    user_docs,
    tender,
    validTenderId,
    handleUpdateTender,
    loadingStates,
    isDraggingAdhoc,
    handleDragEventsAdhoc,
    handleFileUpload,
    uploadValidationWarning,
    processingFiles,
    handleCategoryChange,
    handleDeleteSupportDoc,
    documentCategories,
    handleDownload,
    users,
    account,
  } = useTenderDetails();

  if (!canManageTender) {
    return null;
  }

  return (
    <WorkflowStep
      stepNumber={5}
      expanded={openStep === 5}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 5 : null)}
      title="Manage Documents"
      description="Manage context files for this specific tender."
      isComplete={
        user_docs.length > 0 ||
        Object.keys(tender?.libraryDocOverrides || EMPTY_OBJECT).length > 0 ||
        !!stepCompletion[5]
      }
      onToggleComplete={() => toggleStepCompletion(5)}
      optional={true}
    >
      <SupportDocumentsSection
        tenderId={validTenderId}
        tender={tender}
        onUpdateTender={handleUpdateTender}
        user_docs={user_docs.filter(
          (d: import('../../components/TenderDetails/types').SupportDoc) =>
            d.doc_type !== 'partner_doc' &&
            d.doc_type !== 'people_cv' &&
            d.doc_type !== 'cv' &&
            d.doc_type !== 'cvs'
        )}
        loadingStates={loadingStates}
        isDragging={isDraggingAdhoc}
        onDragEnter={handleDragEventsAdhoc}
        onDragOver={handleDragEventsAdhoc}
        onDragLeave={handleDragEventsAdhoc}
        onFileUpload={(file, collectionType, saveToGlobal, destinationFolder) =>
          handleFileUpload(file, 'support', {
            ...(collectionType !== undefined && { collectionType }),
            ...(saveToGlobal !== undefined && { saveToGlobal }),
            ...(destinationFolder !== undefined && { destinationFolder }),
          })
        }
        uploadValidationWarning={uploadValidationWarning.support}
        processingFiles={processingFiles.support}
        onCategoryChange={handleCategoryChange}
        onDeleteDoc={handleDeleteSupportDoc}
        documentCategories={documentCategories}
      />

      {user_docs.some(
        (d: import('../../components/TenderDetails/types').SupportDoc) =>
          d.doc_type === 'partner_doc'
      ) && (
        <Box sx={{ mt: 6, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                Partner Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                These documents were uploaded by linked external partners. You can allocate their
                type so the AI knows how to use them.
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="relationship-type-label">Relationship Type</InputLabel>
              <Select
                labelId="relationship-type-label"
                label="Relationship Type"
                value={tender.partnerRelationship || ''}
                onChange={(e) => handleUpdateTender('partnerRelationship', e.target.value)}
              >
                <MenuItem value="">
                  <em>None specified</em>
                </MenuItem>
                <MenuItem value="Joint Venture">Joint Venture</MenuItem>
                <MenuItem value="Prime Contractor (We are Prime)">Prime Contractor</MenuItem>
                <MenuItem value="Subcontractor (We are Sub)">Subcontractor</MenuItem>
                <MenuItem value="Consortium">Consortium</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              backgroundColor: 'transparent',
              '& th': {
                backgroundColor: 'greyContrast',
                color: 'primary.contrastText',
                fontWeight: 600,
              },
            }}
          >
            <StandardTable
              columns={[
                {
                  id: 'fileName',
                  label: 'File Name',
                  render: (doc) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {doc.originalFilename || doc.name}
                      </Typography>
                      {(doc.conversionStatus === 'failed' || doc.embeddingStatus === 'failed') && (
                        <Tooltip
                          title={doc.conversionFailureReason || 'Document processing failed'}
                        >
                          <ErrorIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  ),
                },
                {
                  id: 'uploadedBy',
                  label: 'Uploaded By',
                  render: (doc) =>
                    users.find((u: import('../../types/auth').User) => u.uid === doc.uploaderId)
                      ?.displayName ||
                    doc.uploaderId ||
                    'Unknown',
                },
                {
                  id: 'type',
                  label: 'Type',
                  render: (doc) => {
                    const docIdentifier = (
                      doc.originalFilename ||
                      doc.filename ||
                      doc.name ||
                      ''
                    ).replace(/\.[^/.]+$/, '');
                    let currentCategory = 'bidding_proposal';
                    if (documentCategories?.[docIdentifier]) {
                      currentCategory = documentCategories[docIdentifier];
                    } else if (doc.doc_type && doc.doc_type !== 'partner_doc') {
                      currentCategory = doc.doc_type;
                    } else if (doc.originalFilename) {
                      const lowerName = doc.originalFilename.toLowerCase();
                      if (/case\s?study/i.test(lowerName)) {
                        currentCategory = 'bidding_company_profile';
                      }
                    }

                    return (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={currentCategory}
                          onChange={(e) => handleCategoryChange(docIdentifier, e.target.value)}
                        >
                          {DOMAIN_ARCHETYPES.bidding.map((type) => (
                            <MenuItem key={type} value={type}>
                              {ARCHETYPE_LABELS[type]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  },
                },
                {
                  id: 'actions',
                  label: 'Actions',
                  align: 'right',
                  render: (doc) => (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          handleDownload(
                            doc.downloadURL ||
                              (doc.storageUri?.startsWith('gs://') ||
                              doc.gcsPath?.startsWith('gs://')
                                ? doc.storageUri || doc.gcsPath || ''
                                : `gs://${account?.actualDefaultBucketName}/${doc.storageUri || doc.gcsPath || ''}`),
                            'support',
                            doc.originalFilename || doc.name || 'document'
                          )
                        }
                      >
                        Download
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        sx={{ ml: 1 }}
                        disabled={!!loadingStates[`delete_${doc.originalFilename || doc.name}`]}
                        onClick={() => handleDeleteSupportDoc(doc)}
                      >
                        {loadingStates[`delete_${doc.originalFilename || doc.name}`] ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </>
                  ),
                },
              ]}
              data={user_docs.filter(
                (d: import('../../components/TenderDetails/types').SupportDoc) =>
                  d.doc_type === 'partner_doc'
              )}
              keyExtractor={(doc) => doc.id || doc.originalFilename || doc.name || ''}
              emptyStateMessage="No partner documents found."
              size="small"
              stickyHeader={false}
            />
          </Box>
        </Box>
      )}
    </WorkflowStep>
  );
};

export default ManageDocumentsStep;
